<template>
  <v-dialog v-model="visible" max-width="420" persistent>
    <v-card class="pin-modal">
      <v-card-title class="pin-modal__header">
        <v-icon color="primary" class="mr-2">mdi-email-lock-outline</v-icon>
        <span>Verificación de firma</span>
        <v-spacer />
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pin-modal__body">
        <p class="pin-modal__description">
          Ingresa el código de <strong>6 dígitos</strong> que enviamos a tu correo electrónico para confirmar la firma.
        </p>

        <div class="otp-container" @paste="handlePaste">
          <input
            v-for="(digit, index) in digits"
            :key="index"
            :ref="`otpInput${index}`"
            v-model="digits[index]"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="1"
            class="otp-input"
            :class="{ 'otp-input--filled': digits[index], 'otp-input--error': hasError }"
            @input="onInput(index, $event)"
            @keydown="onKeydown(index, $event)"
            @focus="$event.target.select()"
          />
        </div>

        <v-expand-transition>
          <p v-if="hasError" class="pin-modal__error">
            <v-icon color="error" small class="mr-1">mdi-alert-circle-outline</v-icon>
            Código incorrecto o incompleto. Intenta nuevamente.
          </p>
        </v-expand-transition>

        <div class="pin-modal__resend">
          <span>¿No recibiste el código?</span>
          <v-btn
            text
            x-small
            color="primary"
            :disabled="resendCooldown > 0"
            :loading="resending"
            @click="resendPin"
          >
            {{ resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código' }}
          </v-btn>
        </div>
      </v-card-text>

      <v-card-actions class="pin-modal__actions">
        <v-spacer />
        <v-btn text @click="close">Cancelar</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="!isComplete"
          :loading="submitting"
          @click="submitPin"
        >
          Firmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'PinEntryModal',
  props: {
    visible: Boolean,
    submitting: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false }
  },
  data() {
    return {
      digits: ['', '', '', '', '', ''],
      resendCooldown: 0,
      resending: false,
      resendTimer: null
    }
  },
  computed: {
    pin() {
      return this.digits.join('')
    },
    isComplete() {
      return this.digits.every((d) => d !== '') && this.pin.length === 6
    }
  },
  watch: {
    visible(newVal) {
      this.resetPin()
      if (newVal) {
        this.$nextTick(() => this.focusInput(0))
      }
    }
  },
  beforeDestroy() {
    if (this.resendTimer) clearInterval(this.resendTimer)
  },
  methods: {
    resetPin() {
      this.digits = ['', '', '', '', '', '']
    },
    focusInput(index) {
      const ref = this.$refs[`otpInput${index}`]
      if (ref && ref[0]) ref[0].focus()
    },
    onInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, '')
      this.digits.splice(index, 1, value.slice(-1))

      if (value && index < 5) {
        this.focusInput(index + 1)
      }

      if (this.isComplete) {
        this.$nextTick(() => event.target.blur())
      }
    },
    onKeydown(index, event) {
      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        this.focusInput(index - 1)
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1)
      }
      if (event.key === 'ArrowRight' && index < 5) {
        this.focusInput(index + 1)
      }
    },
    handlePaste(event) {
      event.preventDefault()
      const pasted = (event.clipboardData || window.clipboardData)
        .getData('text')
        .replace(/[^0-9]/g, '')
        .slice(0, 6)
        .split('')

      this.digits = ['', '', '', '', '', '']
      pasted.forEach((char, i) => {
        this.digits.splice(i, 1, char)
      })

      const nextIndex = pasted.length < 6 ? pasted.length : 5
      this.$nextTick(() => this.focusInput(nextIndex))
    },
    close() {
      this.resetPin()
      this.$emit('close')
    },
    submitPin() {
      if (!this.isComplete) return
      this.$emit('submit', this.pin)
    },
    resendPin() {
      this.resending = true
      this.$emit('resend')
      this.resendCooldown = 30
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer)
          this.resending = false
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.pin-modal__header {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  padding: 20px 24px 8px;
}

.pin-modal__body {
  padding: 8px 24px 4px;
}

.pin-modal__description {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1.5;
  margin-bottom: 24px;
}

.otp-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 8px;
}

.otp-input {
  width: 44px;
  height: 52px;
  text-align: center;
  font-size: 22px;
  font-weight: 600;
  border: 1.5px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  background: #fafafa;
}

.otp-input:focus {
  border-color: var(--v-primary-base, #1976d2);
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.15);
  background: #fff;
}

.otp-input--filled {
  border-color: rgba(0, 0, 0, 0.3);
  background: #fff;
}

.otp-input--error {
  border-color: #ff5252;
}

.pin-modal__error {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff5252;
  font-size: 13px;
  margin: 4px 0 0;
}

.pin-modal__resend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 20px;
}

.pin-modal__actions {
  padding: 12px 24px 20px;
}
</style>